import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ArrowLeft, Loader2, CheckCircle } from "lucide-react";
import axios from "axios";
import { API } from "../utils/api";

// Fleety Logo Component
const FleetyLogo = ({ className = "", size = "default" }) => {
  const sizeClasses = {
    small: "text-2xl",
    default: "text-4xl md:text-5xl",
    large: "text-5xl md:text-6xl"
  };
  
  return (
    <div className={`fleety-logo ${sizeClasses[size]} ${className}`} data-testid="fleety-logo">
      <span className="text-[#3FA9F5]">F</span>
      <span className="text-[#3FA9F5]">leety</span>
    </div>
  );
};

// Service Area Banner Component
const ServiceAreaBanner = () => (
  <div className="w-full max-w-md mx-auto mb-6 px-4 py-3 bg-[#3FA9F5]/10 rounded-xl border border-[#3FA9F5]/20" data-testid="service-area-banner">
    <p className="text-gray-600 text-sm text-center leading-relaxed">
      🌟 Hey 560075, we're here! ✨<br />
      <span className="text-gray-500">Fleety is live and growing super fast — can't wait to bring the love across Namma Bengaluru soon 🫶</span>
    </p>
  </div>
);

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [phoneNumber, setPhoneNumber] = useState(location.state?.phone || "");
  const [otp, setOtp] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState(location.state?.message || "");
  const [otpSent, setOtpSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  // Countdown timer for resend OTP
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 10);
    setPhoneNumber(value);
    setError("");
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 4);
    setOtp(value);
    setError("");
  };

  const handleGetOtp = async () => {
    if (phoneNumber.length !== 10) {
      setError("Please enter a valid 10-digit phone number");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      // Call backend to send OTP - backend handles MSG91
      const response = await axios.post(`${API}/login-send-otp`, {
        phone_number: phoneNumber
      });

      if (response.data.success) {
        setOtpSent(true);
        setResendTimer(30);
        setSuccessMessage("OTP sent to your phone!");
      } else {
        setError(response.data.message || "Failed to send OTP");
        if (response.data.message?.includes("not registered")) {
          setTimeout(() => navigate("/signup"), 2000);
        }
      }
    } catch (err) {
      console.error("Error:", err);
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length < 4) {
      setError("Please enter a valid 4-digit OTP");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      // Verify OTP via backend
      const response = await axios.post(`${API}/login-verify-otp`, {
        phone_number: phoneNumber,
        otp: otp
      });

      if (response.data.success) {
        localStorage.setItem("fleety_user", JSON.stringify(response.data.user));
        navigate("/home");
      } else {
        setError(response.data.message || "Invalid OTP");
      }
    } catch (err) {
      console.error("Verify error:", err);
      setError(err.response?.data?.detail || "Verification failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = () => {
    if (resendTimer === 0) {
      setOtp("");
      handleGetOtp();
    }
  };

  const handleBack = () => {
    if (otpSent) {
      setOtpSent(false);
      setOtp("");
      setError("");
      setSuccessMessage("");
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="login-page">
      <div className="h-6 bg-[#3FA9F5]"></div>
      
      <header className="bg-[#3FA9F5] p-6 flex items-center gap-4">
        <button 
          onClick={handleBack}
          className="text-white hover:text-white/80 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft size={24} />
        </button>
        <FleetyLogo size="small" className="text-white [&>span]:text-white" />
      </header>

      <main className="flex-1 flex flex-col px-6 pt-10">
        <div className="w-full max-w-md mx-auto">
          <h1 className="font-heading text-3xl font-bold text-gray-900 mb-2">
            {otpSent ? "Enter OTP" : "Log In"}
          </h1>
          <p className="text-gray-500 mb-8">
            {otpSent 
              ? `Enter the OTP sent to +91 ${phoneNumber}`
              : "Enter your phone number to receive OTP"
            }
          </p>

          {successMessage && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3" data-testid="success-message">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span className="text-green-700">{successMessage}</span>
            </div>
          )}

          {!otpSent ? (
            // Phone Number Entry Screen
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <div className="flex gap-2">
                  <div className="h-14 px-4 flex items-center bg-gray-50 border-2 border-gray-100 rounded-xl text-gray-500">
                    +91
                  </div>
                  <Input
                    type="tel"
                    placeholder="Enter 10-digit number"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    className="flex-1 h-14 px-6 text-lg rounded-xl border-2 border-gray-100 bg-gray-50 focus:border-[#3FA9F5] focus:bg-white fleety-input"
                    data-testid="phone-input"
                  />
                </div>
              </div>

              <Button 
                onClick={handleGetOtp}
                disabled={isLoading || phoneNumber.length !== 10}
                className="w-full h-14 bg-[#3FA9F5] hover:bg-[#2196F3] text-white text-lg font-semibold rounded-full shadow-fleety transition-all disabled:opacity-50"
                data-testid="generate-otp-btn"
              >
                {isLoading ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  "Get OTP"
                )}
              </Button>

              {error && (
                <p className="text-red-500 text-sm text-center" data-testid="error-message">{error}</p>
              )}
            </div>
          ) : (
            // OTP Entry Screen
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Enter OTP
                </label>
                <Input
                  type="tel"
                  placeholder="Enter 4-digit OTP"
                  value={otp}
                  onChange={handleOtpChange}
                  className="w-full h-14 px-6 text-xl text-center tracking-[0.5em] rounded-xl border-2 border-gray-100 bg-gray-50 focus:border-[#3FA9F5] focus:bg-white fleety-input"
                  data-testid="otp-input"
                  autoFocus
                  maxLength={4}
                />
              </div>

              <Button 
                onClick={handleVerifyOtp}
                disabled={isLoading || otp.length < 4}
                className="w-full h-14 bg-[#3FA9F5] hover:bg-[#2196F3] text-white text-lg font-semibold rounded-full shadow-fleety transition-all disabled:opacity-50"
                data-testid="verify-otp-btn"
              >
                {isLoading ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  "Verify & Login"
                )}
              </Button>

              {error && (
                <p className="text-red-500 text-sm text-center" data-testid="error-message">{error}</p>
              )}

              {/* Resend OTP */}
              <div className="text-center pt-4">
                {resendTimer > 0 ? (
                  <p className="text-gray-500 text-sm">
                    Resend OTP in <span className="font-semibold text-[#3FA9F5]">{resendTimer}s</span>
                  </p>
                ) : (
                  <button
                    onClick={handleResendOtp}
                    className="text-[#3FA9F5] font-semibold text-sm hover:underline"
                    data-testid="resend-otp-btn"
                  >
                    Resend OTP
                  </button>
                )}
              </div>
            </div>
          )}

          <p className="text-center mt-8 text-gray-500">
            Don't have an account?{" "}
            <button 
              onClick={() => navigate("/signup")}
              className="text-[#3FA9F5] font-semibold hover:underline"
              data-testid="signup-link"
            >
              Sign Up
            </button>
          </p>

          {/* Service Area Banner */}
          <div className="mt-8">
            <ServiceAreaBanner />
          </div>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
